import { Button, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { CONTRACT_DETAILS_PAGE, CONTRACTS_PAGE } from 'src/constants';
import { AUTH_STATES } from 'src/constants/authConsts';
import { RouteContext } from 'src/context';
import { useFeatureFlags } from 'src/context/flagsContext';
import { Contract } from 'src/entities/Contract';
import history from 'src/history';
import { usePageTitle } from 'src/hooks/usePageTitle';
import { PageShell } from 'src/legacy/components/Layout';
import { TableSearch } from 'src/legacy/components/SearchInput/Searchbox';
import { NonDesktopActionTooltip } from 'src/legacy/components/Tooltip';
import { PageToolbar, PageToolbarAction } from 'src/legacy/components/UI';
import { BaseTabsHeader } from 'src/legacy/components/UI/BaseTab';
import { BreadCrumbBase } from 'src/legacy/components/UI/BreadCrumbBase';
import { TableToolbar } from 'src/legacy/components/UI/Pages/TableToolbar';
import { ContractPreviewModal } from 'src/routes/contracts/components/modals/preview/ContractPreviewModal';
import { useGetContractTemplatesQuery } from 'src/services/api';
import { RootState } from 'src/store';
import { openAuthModal } from 'src/store/ui/actions';
import { ensureUnreachable } from 'src/utils/common_utils';

import { ContractBuilderAndSignerForm } from './ContractBuilderAndSignerForm';
import { ShareContractModal } from './ShareContractModal';
import { SubmissionAndRequestTable } from './SubmissionAndRequestTable';

export enum ContractDetailsPageView {
  Edit = 'edit',
  SubmissionRequests = 'submissions_requests',
}

export type ContractDetailsPageQueryParams = {
  view: ContractDetailsPageView;
  templateId: string;
};

const useStyles = makeStyles()((theme) => ({
  breadcrumbWrapper: {
    [theme.breakpoints.down('xs')]: {
      width: 130,
    },
  },
}));

export const ContractDetailsPage = () => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const { query } = useContext(RouteContext);
  const isFormPageChanged = useSelector(
    (state: RootState) => state.ui.isFormPageChanged,
  );

  const { view = ContractDetailsPageView.SubmissionRequests, templateId } =
    query as ContractDetailsPageQueryParams;

  const { EnableContextualActions } = useFeatureFlags();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(
    null,
  );
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const theme = useTheme();
  const isDesktopScreen = useMediaQuery(theme.breakpoints.up('md'));

  const { data: templates } = useGetContractTemplatesQuery();

  const selectedTemplate = templates?.find((t) => t.id === templateId);

  const handleTabChange = (key: ContractDetailsPageView) => {
    // If we make any change to edit contract form
    // we need to confirm the user before navigating away
    if (isFormPageChanged) {
      dispatch(
        openAuthModal(
          AUTH_STATES.CONFIRM_PAGE_CHANGE_MODAL,
          `${CONTRACT_DETAILS_PAGE.path}?templateId=${templateId}&view=${key}`,
        ),
      );
      return;
    }
    history.replace({ search: `templateId=${templateId}&view=${key}` });
  };
  const { pageTitle } = usePageTitle(CONTRACTS_PAGE.label, 'contracts');

  const renderActiveTab = () => {
    switch (view) {
      case ContractDetailsPageView.SubmissionRequests:
        return (
          <SubmissionAndRequestTable
            selectedTemplate={selectedTemplate}
            onContractRowClick={
              EnableContextualActions
                ? (contract: Contract) => {
                    setSelectedContract(contract);
                    setIsPreviewModalOpen(true);
                  }
                : undefined
            }
          />
        );
      case ContractDetailsPageView.Edit:
        return <ContractBuilderAndSignerForm />;
      default:
        return ensureUnreachable(view);
    }
  };

  const breadcrumbItems = useMemo(() => {
    return [
      {
        label: pageTitle,
        path: CONTRACTS_PAGE.path,
      },
      {
        label: selectedTemplate?.name || '',
      },
    ];
  }, [selectedTemplate]);

  const pageTabHeaders: Array<{
    key: ContractDetailsPageView;
    label: React.ReactNode;
  }> = useMemo(
    () => [
      {
        key: ContractDetailsPageView.SubmissionRequests,
        label: 'Submissions and requests',
      },
      {
        key: ContractDetailsPageView.Edit,
        label: <NonDesktopActionTooltip>Edit template</NonDesktopActionTooltip>,
        disabled: !isDesktopScreen, // we don't allow edit template on small screens
      },
    ],
    [isDesktopScreen],
  );

  const headerContent = (
    <TableToolbar>
      <PageToolbar
        title={
          <div className={classes.breadcrumbWrapper}>
            <BreadCrumbBase items={breadcrumbItems} showOnlyTitleOnMobile />
          </div>
        }
        actionButtons={[
          <PageToolbarAction
            key="contract-details-actions"
            actions={[]}
            createButton={
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsShareModalOpen(true)}
              >
                Send contract
              </Button>
            }
          >
            <TableSearch />
          </PageToolbarAction>,
        ]}
      />
    </TableToolbar>
  );

  const pageContent = (
    <>
      <BaseTabsHeader
        tabs={pageTabHeaders}
        activeTabKey={view}
        onTabChange={(key) => handleTabChange(key as ContractDetailsPageView)}
      />
      {renderActiveTab()}
      <ShareContractModal
        open={isShareModalOpen}
        selectedTemplate={selectedTemplate}
        onClose={() => setIsShareModalOpen(false)}
      />
    </>
  );

  return (
    <PageShell
      header={headerContent}
      access="allowContractsPage"
      showSpace={false}
    >
      {pageContent}
      {EnableContextualActions && (
        <ContractPreviewModal
          isOpen={isPreviewModalOpen}
          contract={selectedContract}
          onClose={() => {
            setSelectedContract(null);
            setIsPreviewModalOpen(false);
          }}
        />
      )}
    </PageShell>
  );
};
